import { create } from "zustand";

import type { ActiveContext } from "../types";

/**
 * activeContextStore — single source of truth for "what entity the user
 * has currently OPEN in the workspace" (Jun 13, 2026).
 *
 * Pattern
 * -------
 * The inline viewers (CalendarEventPane, document preview, inbox thread,
 * source citation drawer) call ``setActive(ctx)`` on mount and
 * ``clearActive(id)`` on unmount via the ``useActiveContextScope`` hook.
 * That keeps the store always in sync with the rendered UI without any
 * polling, URL parsing, or per-viewer fan-out in the chat layer.
 *
 * ``id`` is passed to ``clearActive`` so an unmount cannot accidentally
 * stomp on a newer viewer that already registered — common during fast
 * navigation when the new viewer mounts before the old one's cleanup
 * effect runs.
 *
 * Why Zustand instead of Context
 * ------------------------------
 * Mirrors ``useProjectsStore`` (project scope) and ``useSourcesStore``
 * (drawer mode) — the chat send path reads via ``getState()`` snapshot
 * so the chat composer never re-renders when the user opens / closes a
 * viewer (no subscription, no churn).
 */
export interface ActiveContextState {
  /** Currently active entity, or ``null`` when the user is on a bare hub. */
  active: ActiveContext | null;
  /**
   * Id the user explicitly DISMISSED from the ambient context chip
   * (Jul 13, 2026). While ``dismissedId === active.id`` the open entity
   * is suppressed from the chat send envelope — the user said "don't use
   * the thing I have open" by clicking the chip's ✕.
   *
   * The store keeps ``active`` truthful (it still reflects what's open —
   * ``useActiveContextScope`` auto-registration is untouched); dismissal
   * is a SEPARATE flag layered on top. Opening a different entity clears
   * the flag (see ``setActive`` / ``clearActive``), so the chip reappears
   * the moment the user moves to a new item.
   */
  dismissedId: string | null;
  /** Register an entity as currently open. Overwrites any prior entry. */
  setActive: (ctx: ActiveContext) => void;
  /**
   * Clear the active entity. Guarded by ``id``: the clear is a no-op when
   * the current entry has a different id (avoids the race when viewer B
   * mounts before viewer A's unmount effect runs).
   */
  clearActive: (id: string) => void;
  /**
   * Suppress the currently-open entity from the NEXT send(s) — invoked by
   * the ambient context chip's ✕. No-op when nothing is open. Reversed
   * automatically when a different entity is opened.
   */
  dismiss: () => void;
}

export const useActiveContextStore = create<ActiveContextState>((set) => ({
  active: null,
  dismissedId: null,
  setActive: (ctx) =>
    // Opening a DIFFERENT entity clears any prior dismissal so the chip
    // re-appears; the same entity re-registering (e.g. a summary refresh)
    // preserves the user's dismissal. Re-opening the same id after a
    // full unmount also re-shows it, because ``clearActive`` reset the
    // flag on the way out.
    set((s) => ({
      active: ctx,
      dismissedId: s.active?.id === ctx.id ? s.dismissedId : null,
    })),
  clearActive: (id) =>
    set((s) =>
      s.active && s.active.id === id
        ? { active: null, dismissedId: null }
        : s,
    ),
  dismiss: () =>
    set((s) => (s.active ? { dismissedId: s.active.id } : s)),
}));

/**
 * Subscription-free snapshot for the chat send pipeline. Reads the
 * latest active context exactly once — no re-render triggered.
 */
export function getActiveContextSnapshot(): ActiveContext | null {
  return useActiveContextStore.getState().active;
}

/**
 * Subscription-free read of the "user dismissed the open entity" flag,
 * mirrored into the send pipeline's drop-logic exactly like
 * ``getActiveContextSnapshot``. ``true`` ⇒ the open entity's
 * ``active_context`` must be dropped from the envelope even though a
 * viewer is still mounted.
 */
export function isActiveContextDismissed(): boolean {
  const s = useActiveContextStore.getState();
  return s.active != null && s.dismissedId === s.active.id;
}
